import { i18n } from "@tellescope/i18n";
import { cn } from "@tellescope/ui";
import { useMemo } from "react";
import { useDateFnsFormattersWithLocale } from "@tellescope/ui";

type EventStatusVariant = "cancelled" | "no-show" | "completed" | "status-update";

const getVariantMap = (): Record<
  EventStatusVariant,
  {
    leadingText: string;
    leadingTextClassName: string;
    getNameText: (name?: string) => string | undefined;
  }
> =>
  ({
    cancelled: {
      leadingText: i18n.t("Cancelled", { ns: "calendar" }),
      leadingTextClassName: cn("font-medium text-red-600"),
      getNameText: (name) => i18n.t(" – by {{name}}", { name, ns: "calendar" }),
    },
    "no-show": {
      leadingText: i18n.t("No-show", { ns: "calendar" }),
      leadingTextClassName: cn("font-medium text-muted-foreground"),
      getNameText: (name) => i18n.t(" – marked by {{name}}", { name, ns: "calendar" }),
    },
    completed: {
      leadingText: i18n.t("Completed", { ns: "calendar" }),
      leadingTextClassName: cn("font-medium text-green-600"),
      getNameText: (name) => i18n.t(" – by {{name}}", { name, ns: "calendar" }),
    },
    "status-update": {
      leadingText: i18n.t("Latest status update", { ns: "calendar" }),
      leadingTextClassName: cn("font-medium text-muted-foreground"),
      getNameText: (name) => i18n.t(" by {{name}}", { name, ns: "calendar" }),
    },
  }) as const;

export interface EventStatusRowProps {
  variant: EventStatusVariant;
  timestamp?: Date;
  name?: string;
}

export const EventStatusRow = ({ variant, timestamp, name }: EventStatusRowProps) => {
  const { format } = useDateFnsFormattersWithLocale();
  const { leadingText, leadingTextClassName, nameText } = useMemo(() => {
    const { leadingText, leadingTextClassName, getNameText } = getVariantMap()[variant];
    const nameText = name ? getNameText(name) : undefined;
    return { leadingText, leadingTextClassName, nameText };
  }, [name, variant]);

  return (
    <div className="text-xs font-light text-muted-foreground">
      <span className={leadingTextClassName}>{leadingText}</span>
      {timestamp ? ` · ${format(timestamp, "MMM d, yyyy")}` : null}
      {nameText ? `${nameText}` : null}
    </div>
  );
};
